// components/MainLayout.jsx
import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useFirebaseAuth } from '../contexts/FirebaseAuthContext';
import { useUsersStore } from '../store/useUsersStore';
import ProposalModal from './ProposalModal';
import { useModal } from '../contexts/ModalContext';
import DashboardSidebar from '../components-dashboard/DashboardSidebar';
import TopBar from '../components-dashboard/TopBar';

const MainLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user: fbUser, logout } = useFirebaseAuth();
  const fetchUsers = useUsersStore((s) => s.fetchUsers);
  const updateSubUser = useUsersStore((s) => s.updateSubUser);
  const users = useUsersStore((s) => s.users);
  const selectedKey = useUsersStore((s) => s.selectedKey);
  const selectedUser = React.useMemo(() => {
    if (!selectedKey || !Array.isArray(users)) return null;
    return (
      users.find((u) => {
        const ids = [
          u.document_id,
          u.sub_user_id,
          u.id,
          u._id,
          u.uid,
          u.sub_username,
          u.username,
        ];
        return ids.some((id) => id && String(id) === String(selectedKey));
      }) || null
    );
  }, [users, selectedKey]);

  const { modalState, closeModal } = useModal();

  const [updatingSubUser, setUpdatingSubUser] = useState(false);
  const [useAiProposal, setUseAiProposal] = useState(() => {
    try {
      return localStorage.getItem('AUTO_BID_USE_AI') === 'true';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!fbUser?.uid) return;
    (async () => {
      try {
        const idToken =
          typeof fbUser.getIdToken === 'function'
            ? await fbUser.getIdToken()
            : null;
        await fetchUsers(fbUser.uid, idToken);
      } catch (err) {
        console.warn('MainLayout: fetchUsers failed', err?.message || err);
      }
    })();
  }, [fbUser, fetchUsers]);

  useEffect(() => {
    try {
      const isAi = selectedUser?.autobid_proposal_type === 'ai';
      setUseAiProposal(isAi);
    } catch {}
  }, [selectedUser]);

  useEffect(() => {
    try {
      localStorage.setItem(
        'AUTO_BID_USE_AI',
        useAiProposal ? 'true' : 'false'
      );
    } catch {}
  }, [useAiProposal]);

  const patchSelectedUser = async (payload) => {
    const target = selectedUser;
    if (!target) {
      alert('Please select an account before updating settings.');
      throw new Error('No selected sub-user');
    }
    setUpdatingSubUser(true);
    try {
      const subUserId =
        target.document_id ||
        target.sub_user_id ||
        target.id ||
        target._id ||
        target.uid;
      await updateSubUser(subUserId, payload);
    } catch (err) {
      console.error('Failed to update sub-user:', err?.message || err);
      alert('Failed to update account settings: ' + (err?.message || err));
      throw err;
    } finally {
      setUpdatingSubUser(false);
    }
  };

  const handleToggleAutoBid = async () => {
    const target = selectedUser;
    if (!target) {
      alert('Please select an account before toggling AutoBid');
      return;
    }
    try {
      const newValue = !target.autobid_enabled;
      await patchSelectedUser({ autobid_enabled: newValue });
    } catch {}
  };

  const handleToggleUseAiProposal = async (checked) => {
    setUseAiProposal(checked);
    try {
      await patchSelectedUser({
        autobid_proposal_type: checked ? 'ai' : 'general',
      });
    } catch {
      setUseAiProposal((prev) => !prev);
    }
  };

  const handleAutoBidTypeChange = async (val) => {
    try {
      await patchSelectedUser({ autobid_enabled_for_job_type: val });
    } catch {}
  };

  // Create dashboard props object to pass to child routes
  const dashboardProps = {
    selectedUser,
    updatingSubUser,
    handleToggleAutoBid,
    handleAutoBidTypeChange,
  };

  return (
    <div className="flex h-screen bg-light-blue-shade-20">
      <DashboardSidebar />

      <div className="flex-1 flex flex-col ml-0 lg:ml-60">
        <TopBar
          userName={fbUser?.displayName || fbUser?.email || 'User'}
          useAiProposal={useAiProposal}
          onToggleUseAiProposal={handleToggleUseAiProposal}
        />

        <div className="flex-1 overflow-auto">
          <main className="p-6 lg:p-8 max-w-7xl mx-auto">
            {/* Pass props to Outlet context */}
            <Outlet context={dashboardProps} />
          </main>
        </div>
      </div>

      {modalState.isOpen && modalState.type === 'proposal' && (
        <ProposalModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          onSubmit={() => {}}
          projectData={modalState.data}
        />
      )}
    </div>
  );
};

export default MainLayout;
